import React, { useState } from "react";
import { Check, Sparkles, Zap, MessageCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { PLAN_META } from "../../lib/plans";

const PLANS = [
  {
    key: "free",
    name: "Free",
    tagline: "Para experimentar sem compromisso",
    monthly: "0",
    annual: "0",
    cta: "Começar grátis",
    features: ["3 orçamentos por mês", "1 empresa / marca", "1 modelo A4", "Geração por IA no app", "PDF com a marca Basiq"],
    highlight: false,
  },
  {
    key: "standard",
    name: "Standard",
    tagline: "Para quem orça toda semana",
    monthly: PLAN_META.standard.monthly,
    annual: PLAN_META.standard.yearly,
    cta: "Testar 7 dias grátis",
    features: ["20 orçamentos por mês", "Orçamentos pelo WhatsApp", "3 empresas / marcas", "5 modelos A4 premium", "Fontes, capa e contracapa", "PDF sem a marca Basiq"],
    highlight: true,
  },
  {
    key: "pro",
    name: "Pro",
    tagline: "Para vender no automático",
    monthly: PLAN_META.pro.monthly,
    annual: PLAN_META.pro.yearly,
    cta: "Testar 7 dias grátis",
    features: ["Orçamentos ilimitados", "Orçamentos pelo WhatsApp", "Empresas / marcas ilimitadas", "Todos os 10 modelos A4", "Google Drive e e-mail", "Suporte prioritário"],
    highlight: false,
  },
];

export default function Pricing({ onCta }) {
  const [annual, setAnnual] = useState(true);
  return (
    <section id="planos" className="relative px-5 py-20 sm:py-28">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 mb-4">Planos</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Simples como deve ser</h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">Comece grátis. Planos pagos têm 7 dias de teste, sem compromisso.</p>

          <div className="mt-7 inline-flex items-center gap-1 bg-white ring-1 ring-slate-200 rounded-full p-1" data-testid="billing-toggle">
            <button onClick={() => setAnnual(false)} data-testid="billing-monthly" className={`text-[13px] font-medium px-4 py-1.5 rounded-full transition-all ${!annual ? "bg-slate-900 text-white" : "text-slate-500"}`}>Mensal</button>
            <button onClick={() => setAnnual(true)} data-testid="billing-annual" className={`text-[13px] font-medium px-4 py-1.5 rounded-full transition-all inline-flex items-center gap-1.5 ${annual ? "bg-slate-900 text-white" : "text-slate-500"}`}>
              Anual <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${annual ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"}`}>até -17%</span>
            </button>
          </div>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.1} className="h-full">
              <div className={`relative h-full rounded-3xl p-7 sm:p-8 flex flex-col ${p.highlight ? "bg-slate-900 text-white shadow-[0_30px_70px_-30px_rgba(15,23,42,0.5)]" : "bg-white ring-1 ring-slate-200"}`} data-testid={`plan-${p.key}`}>
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold text-white shadow-lg" style={{ background: "#4A3BFA" }}>
                    <Sparkles size={12} /> Mais popular
                  </div>
                )}
                <h3 className={`text-xl font-bold tracking-tight ${p.highlight ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
                <p className={`text-[13px] mt-1 ${p.highlight ? "text-slate-300" : "text-slate-500"}`}>{p.tagline}</p>

                <div className="mt-6 flex items-end gap-1.5">
                  <span className={`text-[15px] font-medium ${p.highlight ? "text-slate-300" : "text-slate-400"}`}>R$</span>
                  <span className={`text-5xl font-extrabold tracking-tight ${p.highlight ? "text-white" : "text-slate-900"}`} data-testid={`plan-${p.key}-price`}>{annual ? p.annual : p.monthly}</span>
                  <span className={`text-[14px] mb-1.5 ${p.highlight ? "text-slate-400" : "text-slate-400"}`}>/mês</span>
                </div>
                <div className={`text-[12px] mt-1 h-4 ${p.highlight ? "text-slate-400" : "text-slate-400"}`}>{p.key === "free" ? "para sempre" : annual ? "cobrado anualmente" : "no plano mensal"}</div>

                <button onClick={onCta} data-testid={`plan-${p.key}-cta`}
                  className={`mt-6 h-12 rounded-full text-[15px] font-semibold inline-flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-95 ${p.highlight ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                  <Zap size={16} /> {p.cta}
                </button>

                <div className="mt-7 space-y-3">
                  {p.features.map((f, idx) => {
                    const wa = f.includes("WhatsApp");
                    return (
                      <div key={idx} className="flex items-start gap-2.5">
                        <span className={`grid place-items-center w-5 h-5 rounded-full shrink-0 mt-0.5 ${wa ? "bg-[#25D366] text-white" : p.highlight ? "bg-white/15 text-white" : "bg-slate-900 text-white"}`}>{wa ? <MessageCircle size={12} strokeWidth={2.5} /> : <Check size={12} strokeWidth={3} />}</span>
                        <span className={`text-[14px] ${wa ? "font-semibold " : ""}${p.highlight ? "text-slate-200" : "text-slate-600"}`}>{f}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="text-center text-[12px] text-slate-400 mt-6">Sem fidelidade. Cancele quando quiser, direto no app.</p>
      </div>
    </section>
  );
}
